import { create } from "zustand";
import type { ProviderSelection } from "@rivets/core";

/**
 * Open linked-handoff drafts, keyed by worktree and source chat.
 *
 * Like a "New chat", a handoff outlives the selection: looking at another chat
 * or task keeps what was written, and a task left with its handoff in front
 * comes back to it. Only sending it or cancelling it (Escape) removes it.
 */
export type HandoffDraft = {
  text: string;
  /** The target agent the composer held when it was last unmounted; absent until then. */
  provider?: ProviderSelection;
};

type HandoffDraftsState = {
  drafts: Record<string, HandoffDraft>;
  /** The source chat of the handoff each worktree was left on, while it was the conversation in front. */
  front: Record<string, string>;
  /** Opens the draft if needed and records it as the conversation in front of its worktree. */
  show: (worktreeId: string, sourceChatId: string) => void;
  /** Something other than a handoff is in front of the worktree now. Open drafts stay open. */
  hide: (worktreeId: string) => void;
  setText: (worktreeId: string, sourceChatId: string, text: string) => void;
  /** Keeps the agent an unmounting composer held, while the draft is still open. */
  park: (worktreeId: string, sourceChatId: string, provider: ProviderSelection) => void;
  close: (worktreeId: string, sourceChatId: string) => void;
  reset: () => void;
};

export function handoffDraftKey(worktreeId: string, sourceChatId: string): string {
  return `${worktreeId}:${sourceChatId}`;
}

export const useHandoffDrafts = create<HandoffDraftsState>()((set) => ({
  drafts: {},
  front: {},
  show: (worktreeId, sourceChatId) => set((state) => {
    const key = handoffDraftKey(worktreeId, sourceChatId);
    if (state.drafts[key] && state.front[worktreeId] === sourceChatId) return state;
    return {
      drafts: state.drafts[key] ? state.drafts : { ...state.drafts, [key]: { text: "" } },
      front: { ...state.front, [worktreeId]: sourceChatId },
    };
  }),
  hide: (worktreeId) => set((state) => {
    if (!(worktreeId in state.front)) return state;
    const { [worktreeId]: _hidden, ...front } = state.front;
    return { front };
  }),
  setText: (worktreeId, sourceChatId, text) => set((state) => {
    const key = handoffDraftKey(worktreeId, sourceChatId);
    const current = state.drafts[key];
    // Only an open draft is written to: a composer still typing after a cancel must not reopen it.
    if (!current || current.text === text) return state;
    return { drafts: { ...state.drafts, [key]: { ...current, text } } };
  }),
  park: (worktreeId, sourceChatId, provider) => set((state) => {
    const key = handoffDraftKey(worktreeId, sourceChatId);
    const current = state.drafts[key];
    if (!current) return state;
    return { drafts: { ...state.drafts, [key]: { ...current, provider } } };
  }),
  close: (worktreeId, sourceChatId) => set((state) => {
    const key = handoffDraftKey(worktreeId, sourceChatId);
    if (!state.drafts[key] && state.front[worktreeId] !== sourceChatId) return state;
    const { [key]: _closed, ...drafts } = state.drafts;
    if (state.front[worktreeId] !== sourceChatId) return { drafts };
    const { [worktreeId]: _front, ...front } = state.front;
    return { drafts, front };
  }),
  reset: () => set({ drafts: {}, front: {} }),
}));
